import { buildImgSrc, buildImgSrcSet } from '@/helpers/image-url';
import { CollectionLabel, Icon, Label, Typography, tk, type IconGlyph, TabBar } from '@sunwebgroup/ui-components';
import { styled } from 'styled-components';
import { useGroupResponsibleStore } from '@/scopes/account/group-responsible/infrastructure/useGroupResponsibleStore';
import { useGroupResponsibleDependencies } from '../dependencies';
import { useStore } from 'zustand';
import { identity } from 'ramda';
import { useIntl } from '@/intl';
import { useBookingNumber, useChildBookingNumber, useCollectionLabel } from '@/domain/groups/hooks';
import type { GroupResponsibleStore, GroupResponsibleTabName } from '@/scopes/account/group-responsible/domain/types';

interface Tab {
  value: GroupResponsibleTabName;
  icon: IconGlyph;
  label: string;
}

export function Hero() {
  const intl = useIntl();
  const bookingNumber = useBookingNumber();
  const childBookingNumber = useChildBookingNumber();
  const collectionLabel = useCollectionLabel();
  const groupName = useGroupResponsibleStore((state) => {
    return state.bookingData.group.name;
  });
  const groupImgSrc = useGroupResponsibleStore((state) => {
    return state.bookingData.group.imgSrc;
  });

  const groupLocation = useGroupResponsibleStore((state) => {
    return state.bookingData.group.location;
  });

  const groupPeriod = useGroupResponsibleStore((state) => {
    return state.bookingData.group.period;
  });

  const labels = useGroupResponsibleStore((state) => {
    return state.bookingData.labels;
  });

  const activeTab = useGroupResponsibleStore((state) => {
    return state.activeTab;
  });

  const { groupResponsibleStore } = useGroupResponsibleDependencies();
  const store = useStore(groupResponsibleStore, identity<GroupResponsibleStore>);

  const tabs: Tab[] = [
    { value: 'booking', icon: 'IconSuitcase', label: 'Boeking' },
    { value: 'participants', icon: 'IconGroup', label: 'Deelnemers' },
    { value: 'rooms', icon: 'IconBed', label: 'Kamers' },
    { value: 'bus', icon: 'IconBusFront', label: 'Busindeling' },
  ];

  const handleTabChange = (value: string) => {
    if (value === 'booking' && bookingNumber) {
      const queryParams = new URLSearchParams({ bookingNumber });
      if (childBookingNumber) {
        queryParams.append('childBooking', childBookingNumber);
      }

      window.location.href = `/mijn-sunweb/boeking?${queryParams.toString()}`;
      return;
    }

    store.changeTab(value as GroupResponsibleTabName);
  };

  return (
    <HeroContainer>
      {groupImgSrc ? (
        <BackgroundImage
          src={buildImgSrc(groupImgSrc, 1920, 399)}
          srcSet={buildImgSrcSet(groupImgSrc, [375, 600, 720, 1440, 1440, 2000, 2400])}
          sizes="100vw"
          alt=""
          // @ts-expect-error -- Not part of the types yet, but this is an img attribute in modern browsers
          fetchpriority="high"
        />
      ) : null}
      <HeroContentContainer>
        <HeroTextContainer>
          <HeroTitle>{groupName}</HeroTitle>
          <Typography>{groupLocation}</Typography>
          <Typography>{groupPeriod}</Typography>
          <HeroLabelsContainer>
            {collectionLabel && <CollectionLabel $type={collectionLabel.type}>{collectionLabel.text}</CollectionLabel>}
            <Label $state="info">{intl.formatMessage({ id: 'groups_responsible__group_booking' })}</Label>
            {labels?.map((label, index) => (
              <Label key={index} $state={label.type}>
                {intl.formatMessage({ id: label.text }, { days: label.placeholders?.days })}
              </Label>
            ))}
          </HeroLabelsContainer>
        </HeroTextContainer>
        <HeroTabBar defaultActive={activeTab} onActiveChange={({ value }) => handleTabChange(value as string)}>
          {tabs.map((tab) => (
            <TabBar.Tab key={tab.value} value={tab.value}>
              <Icon glyph={tab.icon} />
              {tab.label}
            </TabBar.Tab>
          ))}
        </HeroTabBar>
      </HeroContentContainer>
    </HeroContainer>
  );
}

const HeroContentContainer = styled.div`
  position: relative;
  background-color: #fff;
  margin: 0 auto;
  width: fit-content;
  max-width: 720px;
  min-width: 290px;
  display: flex;
  align-items: center;
  flex-direction: column;
  text-align: center;
  border-radius: ${tk('radius_large')};
  color: ${tk('color_semantic_text')};

  @media ${tk('mq_desktop_sm')} {
    padding: ${tk('spacing_lg')} 61px;
  }
`;

const HeroTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${tk('spacing_md')};
  @media ${tk('mq_desktop_sm')} {
    padding: 0px;
  }
`;

const HeroTitle = styled(Typography)`
  ${Typography.modVariant('heading-md')}
  @media ${tk('mq_desktop_sm')} {
    ${Typography.modVariant('heading-xl')}
  }
`;

const HeroLabelsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${tk('spacing_2xs')};
  justify-content: center;
  margin-top: ${tk('spacing_xs')};

  &:empty {
    margin-top: 0;
  }
`;

const HeroContainer = styled.div`
  position: relative;
  padding: ${tk('spacing_3xl')} ${tk('spacing_md')};
  margin: 0 auto;
  width: 100%;
  max-width: 1600px;

  @media ${tk('mq_desktop_sm')} {
    padding: ${tk('spacing_3xl')};
  }
`;

const BackgroundImage = styled.img`
  object-fit: cover;
  position: absolute;
  width: 100%;
  margin: 0 auto;
  height: 100%;
  top: 0;
  left: 0;
`;

const HeroTabBar = styled(TabBar)`
  margin-top: 0px;

  @media ${tk('mq_desktop_sm')} {
    margin-top: 28px;
  }

  @media (max-width: ${tk('breakpoint_desktop_sm')}) {
    border-radius: 0px 0px ${tk('radius_large')} ${tk('radius_large')};
    border-width: 0px;
    border-top-width: 2px;
  }
`;
